import { useLazyQuery } from "@apollo/client";
import { useEffect } from "react";
import { FormattedMessage, useIntl } from "react-intl";

import ActionIconButton from "@components/buttons/ActionIconButton";
import DateAndTimeStamp from "@components/dates/DateAndTimeStamp";
import HandleQuery from "@components/HandleQuery";
import Table, { TableHeader } from "@components/Table";
import useMyExpert from "@hooks/useMyExpert";
import useNodesFromConnection from "@hooks/useNodesFromConnection";
import {
  AutomationSortByEnum,
  GetAutomationApplicationsDocument,
  GetAutomationApplicationsQuery,
  GetAutomationApplicationsQueryVariables
} from "@typing/Generated";
import { getCareProfileName } from "@utils/modelUtils";

type Props = {
  automationId: string;
};

const AutomationApplicationsTable = ({ automationId }: Props) => {
  const intl = useIntl();
  const myExpert = useMyExpert();

  const [getAutomationApplications, getAutomationApplicationsQuery] = useLazyQuery<
    GetAutomationApplicationsQuery,
    GetAutomationApplicationsQueryVariables
  >(GetAutomationApplicationsDocument);

  useEffect(() => {
    getAutomationApplications({ variables: { automationId } });
  }, [automationId, getAutomationApplications]);

  const automationApplications = useNodesFromConnection(getAutomationApplicationsQuery.data?.automationApplications);

  const tableHeaders: TableHeader<AutomationSortByEnum>[] = [
    { messageKey: "models.automationApplication.tableHeadings.owner" },
    { messageKey: "models.automationApplication.tableHeadings.lovedOne" },
    { messageKey: "models.automationApplication.tableHeadings.date" },
    { messageKey: "models.automationApplication.tableHeadings.status" },
    { messageKey: "models.automationApplication.tableHeadings.actions" }
  ];

  return (
    <HandleQuery query={getAutomationApplicationsQuery}>
      {data => (
        <Table<AutomationSortByEnum>
          fetchMore={getAutomationApplicationsQuery.fetchMore}
          headerKey="pages.admin.automations.tableHeadings"
          headers={tableHeaders}
          pageInfo={data?.automationApplications.pageInfo}
        >
          {automationApplications.map(automationApplication => (
            <tr key={automationApplication.id}>
              <td>{automationApplication.journey.owner.fullName}</td>
              <td>{getCareProfileName({ intl, journey: automationApplication.journey })}</td>
              <td>
                <DateAndTimeStamp showWeekday={false} value={automationApplication.createdAt} />
              </td>
              <td>
                <FormattedMessage id={`models.automationApplication.status.${automationApplication.status}`} />
              </td>
              <td>
                <ActionIconButton
                  ariaLabelKey="accessibility.ariaLabels.edit"
                  icon="diagramProject"
                  routerLink={`/${myExpert ? "expert" : "admin"}/automations/${
                    automationApplication.automation.id
                  }/applications/${automationApplication.id}`}
                />
              </td>
            </tr>
          ))}
        </Table>
      )}
    </HandleQuery>
  );
};

export default AutomationApplicationsTable;
